import {
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  approveInternalAuditChecklist,
  approveInternalAuditPlan,
  approveInternalAuditReport,
  createInternalAuditChecklist,
  createInternalAuditPlan,
  createInternalAuditReport,
  deleteInternalAuditChecklist,
  deleteInternalAuditChecklistAttachment,
  deleteInternalAuditPlan,
  deleteInternalAuditPlanAttachment,
  deleteInternalAuditReport,
  deleteInternalAuditReportAttachment,
  downloadInternalAuditChecklistAttachment,
  downloadInternalAuditPlanAttachment,
  downloadInternalAuditReportAttachment,
  fetchInternalAuditChecklists,
  fetchInternalAuditPlans,
  fetchInternalAuditReports,
  revokeInternalAuditChecklist,
  revokeInternalAuditPlan,
  revokeInternalAuditReport,
  updateInternalAuditChecklist,
  updateInternalAuditPlan,
  updateInternalAuditReport,
  uploadInternalAuditChecklistAttachments,
  uploadInternalAuditPlanAttachments,
  uploadInternalAuditReportAttachments,
  downloadInternalAuditPlanPdf,
  downloadInternalAuditChecklistPdf,
  downloadInternalAuditReportPdf,
} from "../../api/internalAudit";
import { notifyError, reportError } from "../../utils/notify";
import { fetchOrgMembers } from "../../api/org";
import { useCompanyStore } from "../../store/companyStore";
import {
  APPROVAL_TARGET_INTERNAL_AUDIT_CHECKLIST,
  APPROVAL_TARGET_INTERNAL_AUDIT_PLAN,
  APPROVAL_TARGET_INTERNAL_AUDIT_REPORT,
} from "../../types/approval";
import {
  canConfirmInspection,
  companyRoleLabel,
} from "../../types/companyRole";
import {
  InternalAuditAttachment,
  InternalAuditChecklist,
  InternalAuditChecklistItem,
  InternalAuditChecklistPayload,
  InternalAuditPlan,
  InternalAuditPlanPayload,
  InternalAuditReport,
  InternalAuditReportPayload,
} from "../../types/internalAudit";
import { OrgMemberSummary } from "../../types/org";
import ApprovalCancelButton from "../approval/ApprovalCancelButton";
import ApprovalRequestModal from "../approval/ApprovalRequestModal";

import "../product/InspectionFormSheet.css";
import "../product/NonconformanceReportPanel.css";
import "./EducationAnnualPlanTab.css";
import "./QualityShell.css";
import "./SmallGroupPanel.css";

type Tab = "plan" | "checklist" | "report";

type PlanView =
  | { type: "list" }
  | { type: "create" }
  | { type: "edit"; planId: number }
  | { type: "detail"; planId: number };

type ChecklistView =
  | { type: "list" }
  | { type: "create" }
  | { type: "edit"; checklistId: number }
  | { type: "detail"; checklistId: number };

type ReportView =
  | { type: "list" }
  | { type: "create" }
  | { type: "edit"; reportId: number }
  | { type: "detail"; reportId: number };

const PLAN_LIST: PlanView = { type: "list" };
const CHECKLIST_LIST: ChecklistView = { type: "list" };
const REPORT_LIST: ReportView = { type: "list" };

type ApprovalStamp = {
  authorName?: string | null;
  reviewerName?: string | null;
  approverName?: string | null;
  canRevoke?: boolean | null;
  approvalInProgress?: boolean | null;
  pendingApprovalRequestId?: number | null;
  canCancelApproval?: boolean | null;
};

const EMPTY_STAMP: ApprovalStamp = {
  authorName: null,
  reviewerName: null,
  approverName: null,
  canRevoke: false,
  approvalInProgress: false,
  pendingApprovalRequestId: null,
  canCancelApproval: false,
};

function newId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function isApproved(status?: string | null) {
  return (status || "").toUpperCase() === "APPROVED";
}

function isDraft(status?: string | null) {
  return !status || status.toUpperCase() === "DRAFT";
}

function StampBox({
  authorName,
  reviewerName,
  approverName,
}: {
  authorName?: string | null;
  reviewerName?: string | null;
  approverName?: string | null;
}) {
  return (
    <div className="ifs-sheet__stamps">
      {(
        [
          ["작성", authorName],
          ["검토", reviewerName],
          ["승인", approverName],
        ] as const
      ).map(([label, value]) => (
        <div key={label}>
          <strong>{label}</strong>
          <span
            style={{
              color: value ? "#1f2a37" : "#98a2b3",
              fontWeight: value ? 600 : 400,
            }}
          >
            {value || "-"}
          </span>
        </div>
      ))}
    </div>
  );
}

function stampFrom(row: ApprovalStamp): ApprovalStamp {
  return {
    authorName: row.authorName ?? null,
    reviewerName: row.reviewerName ?? null,
    approverName: row.approverName ?? null,
    canRevoke: row.canRevoke ?? false,
    approvalInProgress: row.approvalInProgress ?? false,
    pendingApprovalRequestId: row.pendingApprovalRequestId ?? null,
    canCancelApproval: row.canCancelApproval ?? false,
  };
}

function apiErrorMessage(err: unknown, fallback: string) {
  return (
    (err as { response?: { data?: { message?: string } } })?.response?.data
      ?.message ?? fallback
  );
}

function memberName(m: OrgMemberSummary) {
  return m.displayName || m.nickname || `사용자 ${m.userId}`;
}

function memberMeta(m: OrgMemberSummary) {
  const job = m.jobTitle?.trim() || companyRoleLabel(m.role);
  const dept = m.departmentName?.trim() || "미배치";
  return `${job} · ${dept}`;
}

function toggleId(list: number[], id: number) {
  return list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
}

function emptyChecklistItem(): InternalAuditChecklistItem {
  return {
    id: newId(),
    standard: "ISO",
    clause: "",
    question: "",
    evidence: "",
    result: "",
    finding: "",
  };
}

function parseItems(json?: string | null): InternalAuditChecklistItem[] {
  if (!json?.trim()) return [];
  try {
    const parsed = JSON.parse(json);
    if (!Array.isArray(parsed) || parsed.length === 0) return [];
    return parsed.map((row: Partial<InternalAuditChecklistItem>) => ({
      id: row.id || newId(),
      standard: row.standard || "ISO",
      clause: row.clause || "",
      question: row.question || "",
      evidence: row.evidence || "",
      result: row.result || "",
      finding: row.finding || "",
    }));
  } catch {
    return [];
  }
}

function countResults(items: InternalAuditChecklistItem[]) {
  let conform = 0;
  let ofi = 0;
  let nc = 0;
  items.forEach((item) => {
    if (item.result === "CONFORM") conform += 1;
    else if (item.result === "OFI") ofi += 1;
    else if (item.result === "NC") nc += 1;
  });
  return { conform, ofi, nc };
}

function planStatusLabel(
  status?: string | null,
  approvalInProgress?: boolean | null,
) {
  if (approvalInProgress) return "결재 요청 중";
  switch (status) {
    case "APPROVED":
      return "승인";
    case "CONFIRMED":
      return "계획확정";
    case "IN_PROGRESS":
      return "진행중";
    case "DONE":
      return "완료";
    default:
      return "작성중";
  }
}

function checklistStatusLabel(
  status?: string | null,
  approvalInProgress?: boolean | null,
) {
  if (approvalInProgress) return "결재 요청 중";
  switch (status) {
    case "APPROVED":
      return "승인";
    case "COMPLETED":
      return "완료";
    default:
      return "작성중";
  }
}

function reportStatusLabel(
  status?: string | null,
  approvalInProgress?: boolean | null,
) {
  if (approvalInProgress) return "결재 요청 중";
  switch (status) {
    case "APPROVED":
      return "승인";
    case "ISSUED":
      return "발행";
    default:
      return "작성중";
  }
}

function formatSize(size?: number | null) {
  if (size == null || Number.isNaN(size)) return "";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function InternalAuditPanel() {
  const companyId = useCompanyStore((s) => s.companyId);
  const role = useCompanyStore((s) => s.role);
  const canApproveDirect = canConfirmInspection(role);
  const [tab, setTab] = useState<Tab>("plan");
  const [error, setError] = useState("");
  const [approvalMessage, setApprovalMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [orgMembers, setOrgMembers] = useState<OrgMemberSummary[]>([]);

  const [plans, setPlans] = useState<InternalAuditPlan[]>([]);
  const [planKeyword, setPlanKeyword] = useState("");
  const [planView, setPlanView] = useState<PlanView>(PLAN_LIST);
  const [planHint, setPlanHint] = useState("");
  const [planStamp, setPlanStamp] = useState<ApprovalStamp>(EMPTY_STAMP);
  const [planApprovalOpen, setPlanApprovalOpen] = useState(false);
  const [planAttachments, setPlanAttachments] = useState<
    InternalAuditAttachment[]
  >([]);
  const [planPendingFiles, setPlanPendingFiles] = useState<File[]>([]);
  const planFileRef = useRef<HTMLInputElement>(null);
  const [planForm, setPlanForm] = useState<InternalAuditPlanPayload>({
    planNo: "",
    planYear: new Date().getFullYear(),
    auditType: "REGULAR",
    title: "",
    purpose: "품질경영시스템 적합성·유효성 확인 (ISO 9001 / KS)",
    scopeText: "",
    criteriaText: "ISO 9001, KS, 자사 품질매뉴얼/절차서",
    periodFrom: "",
    periodTo: "",
    auditeeDept: "",
    auditeeManager: "",
    leadAuditorUserId: null,
    auditorUserIds: [],
    status: "DRAFT",
    remark: "",
  });

  const [checklists, setChecklists] = useState<InternalAuditChecklist[]>([]);
  const [checklistFilterPlanId, setChecklistFilterPlanId] = useState<
    number | ""
  >("");
  const [checklistView, setChecklistView] = useState<ChecklistView>(CHECKLIST_LIST);
  const [checklistHint, setChecklistHint] = useState("");
  const [checklistStamp, setChecklistStamp] =
    useState<ApprovalStamp>(EMPTY_STAMP);
  const [checklistApprovalOpen, setChecklistApprovalOpen] = useState(false);
  const [checklistAttachments, setChecklistAttachments] = useState<
    InternalAuditAttachment[]
  >([]);
  const [checklistPendingFiles, setChecklistPendingFiles] = useState<File[]>(
    [],
  );
  const checklistFileRef = useRef<HTMLInputElement>(null);
  const [checklistItems, setChecklistItems] = useState<
    InternalAuditChecklistItem[]
  >([]);
  const [checklistForm, setChecklistForm] =
    useState<InternalAuditChecklistPayload>({
      planId: null,
      checklistNo: "",
      title: "내부심사 체크시트",
      auditDate: new Date().toISOString().slice(0, 10),
      auditorName: "",
      status: "DRAFT",
      remark: "",
    });

  const [reports, setReports] = useState<InternalAuditReport[]>([]);
  const [reportFilterPlanId, setReportFilterPlanId] = useState<number | "">("");
  const [reportView, setReportView] = useState<ReportView>(REPORT_LIST);
  const [reportHint, setReportHint] = useState("");
  const [reportStamp, setReportStamp] = useState<ApprovalStamp>(EMPTY_STAMP);
  const [reportApprovalOpen, setReportApprovalOpen] = useState(false);
  const [reportAttachments, setReportAttachments] = useState<
    InternalAuditAttachment[]
  >([]);
  const [reportPendingFiles, setReportPendingFiles] = useState<File[]>([]);
  const reportFileRef = useRef<HTMLInputElement>(null);
  const [reportForm, setReportForm] = useState<InternalAuditReportPayload>({
    planId: null,
    checklistId: null,
    reportNo: "",
    title: "내부심사 보고서",
    auditDate: new Date().toISOString().slice(0, 10),
    reportDate: new Date().toISOString().slice(0, 10),
    scopeSummary: "",
    teamText: "",
    conformCount: 0,
    ofiCount: 0,
    ncCount: 0,
    findingsText: "",
    conclusion: "",
    followUpDue: "",
    ncrRefs: "",
    auditeeConfirm: "",
    status: "DRAFT",
    remark: "",
  });

  const planEditId =
    planView.type === "edit" || planView.type === "detail"
      ? planView.planId
      : null;
  const checklistEditId =
    checklistView.type === "edit" || checklistView.type === "detail"
      ? checklistView.checklistId
      : null;
  const reportEditId =
    reportView.type === "edit" || reportView.type === "detail"
      ? reportView.reportId
      : null;
  const showPlanForm = planView.type !== "list";
  const showChecklistForm = checklistView.type !== "list";
  const showReportForm = reportView.type !== "list";
  const isPlanDetail = planView.type === "detail";
  const isChecklistDetail = checklistView.type === "detail";
  const isReportDetail = reportView.type === "detail";
  const isPlanEditing = planView.type === "create" || planView.type === "edit";
  const isChecklistEditing =
    checklistView.type === "create" || checklistView.type === "edit";
  const isReportEditing =
    reportView.type === "create" || reportView.type === "edit";
  const planAttachmentsLocked =
    isApproved(planForm.status) ||
    Boolean(planStamp.approvalInProgress) ||
    (!isPlanEditing && !isDraft(planForm.status));
  const checklistAttachmentsLocked =
    isApproved(checklistForm.status) ||
    Boolean(checklistStamp.approvalInProgress) ||
    (!isChecklistEditing && !isDraft(checklistForm.status));
  const reportAttachmentsLocked =
    isApproved(reportForm.status) ||
    Boolean(reportStamp.approvalInProgress) ||
    (!isReportEditing && !isDraft(reportForm.status));

  const loadPlans = useCallback(async (q?: string) => {
    try {
      setPlans(await fetchInternalAuditPlans(q));
      setError("");
    } catch {
      reportError(setError, "심사 계획서를 불러오지 못했습니다.");
    }
  }, []);

  const loadChecklists = useCallback(async (planId?: number | "") => {
    try {
      setChecklists(
        await fetchInternalAuditChecklists(
          planId === "" || planId == null ? undefined : planId,
        ),
      );
      setError("");
    } catch {
      reportError(setError, "체크시트를 불러오지 못했습니다.");
    }
  }, []);

  const loadReports = useCallback(async (planId?: number | "") => {
    try {
      setReports(
        await fetchInternalAuditReports(
          planId === "" || planId == null ? undefined : planId,
        ),
      );
      setError("");
    } catch {
      reportError(setError, "심사 보고서를 불러오지 못했습니다.");
    }
  }, []);

  const loadOrgMembers = useCallback(async () => {
    try {
      setOrgMembers(await fetchOrgMembers());
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    void loadPlans();
    void loadChecklists();
    void loadReports();
    void loadOrgMembers();
  }, [companyId, loadPlans, loadChecklists, loadReports, loadOrgMembers]);

  useEffect(() => {
    const t = window.setTimeout(() => void loadPlans(planKeyword), 250);
    return () => window.clearTimeout(t);
  }, [planKeyword, loadPlans]);

  useEffect(() => {
    void loadChecklists(checklistFilterPlanId);
  }, [checklistFilterPlanId, loadChecklists]);

  useEffect(() => {
    void loadReports(reportFilterPlanId);
  }, [reportFilterPlanId, loadReports]);

  useEffect(() => {
    if (!showPlanForm || planEditId == null) return;
    const latest = plans.find((r) => r.planId === planEditId);
    if (!latest) return;
    if (latest.attachments) setPlanAttachments(latest.attachments);
    setPlanStamp(stampFrom(latest));
    setPlanForm((p) => ({ ...p, status: latest.status || "DRAFT" }));
  }, [plans, planEditId, showPlanForm]);

  useEffect(() => {
    if (!showChecklistForm || checklistEditId == null) return;
    const latest = checklists.find((r) => r.checklistId === checklistEditId);
    if (!latest) return;
    if (latest.attachments) setChecklistAttachments(latest.attachments);
    setChecklistStamp(stampFrom(latest));
    setChecklistForm((p) => ({ ...p, status: latest.status || "DRAFT" }));
  }, [checklists, checklistEditId, showChecklistForm]);

  useEffect(() => {
    if (!showReportForm || reportEditId == null) return;
    const latest = reports.find((r) => r.reportId === reportEditId);
    if (!latest) return;
    if (latest.attachments) setReportAttachments(latest.attachments);
    setReportStamp(stampFrom(latest));
    setReportForm((p) => ({ ...p, status: latest.status || "DRAFT" }));
  }, [reports, reportEditId, showReportForm]);

  const orgMemberMap = useMemo(() => {
    const map = new Map<number, OrgMemberSummary>();
    orgMembers.forEach((m) => map.set(m.userId, m));
    return map;
  }, [orgMembers]);

  const resetForms = () => {
    setPlanView(PLAN_LIST);
    setChecklistView(CHECKLIST_LIST);
    setReportView(REPORT_LIST);
    setError("");
  };

  const openPlanCreate = () => {
    const year = new Date().getFullYear();
    setPlanStamp(EMPTY_STAMP);
    setPlanApprovalOpen(false);
    setPlanAttachments([]);
    setPlanPendingFiles([]);
    setPlanForm({
      planNo: "",
      planYear: year,
      auditType: "REGULAR",
      title: `${year}년 내부심사 계획서`,
      purpose: "품질경영시스템 적합성·유효성 확인 (ISO 9001 / KS)",
      scopeText: "",
      criteriaText: "ISO 9001, KS, 자사 품질매뉴얼/절차서",
      periodFrom: "",
      periodTo: "",
      auditeeDept: "",
      auditeeManager: "",
      leadAuditorUserId: null,
      auditorUserIds: [],
      status: "DRAFT",
      remark: "",
    });
    setPlanHint("저장하면 상세보기로 이동합니다.");
    setPlanView({ type: "create" });
    void loadOrgMembers();
  };

  const fillPlanForm = (row: InternalAuditPlan) => {
    setPlanStamp(stampFrom(row));
    setPlanApprovalOpen(false);
    setPlanHint("");
    setPlanAttachments(row.attachments || []);
    setPlanPendingFiles([]);
    setPlanForm({
      planNo: row.planNo || "",
      planYear: row.planYear || new Date().getFullYear(),
      auditType: row.auditType || "REGULAR",
      title: row.title || "",
      purpose: row.purpose || "",
      scopeText: row.scopeText || "",
      criteriaText: row.criteriaText || "",
      periodFrom: row.periodFrom || "",
      periodTo: row.periodTo || "",
      auditeeDept: row.auditeeDept || "",
      auditeeManager: row.auditeeManager || "",
      leadAuditorUserId: row.leadAuditorUserId ?? null,
      auditorUserIds: row.auditorUserIds || [],
      status: row.status || "DRAFT",
      remark: row.remark || "",
    });
    void loadOrgMembers();
  };

  const openPlanDetail = (row: InternalAuditPlan) => {
    fillPlanForm(row);
    setPlanView({ type: "detail", planId: row.planId });
  };

  const savePlan = async (e: FormEvent) => {
    e.preventDefault();
    if (isApproved(planForm.status)) {
      reportError(setError, "승인 완료된 계획서는 수정할 수 없습니다.");
      return;
    }
    if (!planForm.leadAuditorUserId) {
      reportError(setError, "주심사원을 선택해 주세요.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...planForm,
        auditorUserIds: Array.from(
          new Set([
            ...(planForm.auditorUserIds || []),
            planForm.leadAuditorUserId,
          ]),
        ),
      };
      let planId = planEditId;
      let saved: InternalAuditPlan | null = null;
      if (planId != null) {
        saved = await updateInternalAuditPlan(planId, payload);
      } else {
        saved = await createInternalAuditPlan(payload);
        planId = saved.planId;
        window.alert(`계획서번호 ${saved.planNo || "-"}로 등록되었습니다.`);
      }
      if (planPendingFiles.length > 0 && planId != null) {
        const uploaded = await uploadInternalAuditPlanAttachments(
          planId,
          planPendingFiles,
        );
        saved = {
          ...saved,
          attachments: [...(saved.attachments || []), ...uploaded],
        };
        setPlanPendingFiles([]);
      }
      fillPlanForm(saved);
      setPlanForm((p) => ({
        ...p,
        planNo: saved!.planNo || p.planNo,
        status: saved!.status || p.status,
      }));
      setPlanStamp(stampFrom(saved));
      setPlanAttachments(saved.attachments || []);
      setPlanView({ type: "detail", planId: saved.planId });
      await loadPlans(planKeyword);
    } catch {
      reportError(setError, "심사 계획서 저장에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  const approvePlan = async () => {
    if (planEditId == null) return;
    if (!canApproveDirect) {
      reportError(setError, "바로 승인은 오너/팀장만 가능합니다.");
      return;
    }
    if (
      !window.confirm(
        "계획서를 바로 승인할까요? 승인 후에는 수정·삭제할 수 없습니다.",
      )
    ) {
      return;
    }
    try {
      setApproving(true);
      setError("");
      const saved = await approveInternalAuditPlan(planEditId);
      setPlanStamp(stampFrom(saved));
      setPlanForm((p) => ({ ...p, status: saved.status || "APPROVED" }));
      setApprovalMessage("바로 승인되었습니다.");
      await loadPlans(planKeyword);
    } catch (err: unknown) {
      reportError(setError, apiErrorMessage(err, "바로 승인에 실패했습니다."));
    } finally {
      setApproving(false);
    }
  };

  const revokePlan = async () => {
    if (planEditId == null) return;
    if (!window.confirm("승인을 철회할까요?\n승인란·검토란이 지워집니다."))
      return;
    try {
      setApproving(true);
      setError("");
      const saved = await revokeInternalAuditPlan(planEditId);
      setPlanStamp(stampFrom(saved));
      setPlanForm((p) => ({ ...p, status: saved.status || "DRAFT" }));
      setApprovalMessage("승인이 철회되었습니다.");
      await loadPlans(planKeyword);
    } catch (err: unknown) {
      reportError(setError, apiErrorMessage(err, "승인 철회에 실패했습니다."));
    } finally {
      setApproving(false);
    }
  };

  const downloadPlanPdf = async () => {
    if (planEditId == null) return;
    try {
      setPdfDownloading(true);
      const name = planForm.planNo?.trim() || String(planEditId);
      await downloadInternalAuditPlanPdf(
        planEditId,
        `내부감사계획_${name}.pdf`,
      );
    } catch (err) {
      notifyError(
        err instanceof Error
          ? err.message
          : "PDF 다운로드에 실패했습니다. Gotenberg 실행 여부를 확인해 주세요.",
      );
    } finally {
      setPdfDownloading(false);
    }
  };

  const handlePlanUploadNow = async (files: FileList | null) => {
    if (!files?.length || planAttachmentsLocked) return;
    const selected = Array.from(files);
    if (planFileRef.current) planFileRef.current.value = "";
    if (planEditId == null) {
      setPlanPendingFiles((prev) => [...prev, ...selected]);
      return;
    }
    setSaving(true);
    try {
      const uploaded = await uploadInternalAuditPlanAttachments(
        planEditId,
        selected,
      );
      setPlanAttachments((prev) => [...prev, ...uploaded]);
      await loadPlans(planKeyword);
    } catch {
      reportError(setError, "첨부파일 업로드에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePlanAttachment = async (
    attachment: InternalAuditAttachment,
  ) => {
    if (planEditId == null || planAttachmentsLocked) return;
    if (!window.confirm(`"${attachment.fileName}" 첨부를 삭제할까요?`)) return;
    setSaving(true);
    try {
      await deleteInternalAuditPlanAttachment(
        planEditId,
        attachment.attachmentId,
      );
      setPlanAttachments((prev) =>
        prev.filter((a) => a.attachmentId !== attachment.attachmentId),
      );
      await loadPlans(planKeyword);
    } catch {
      reportError(setError, "첨부파일 삭제에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  const openChecklistCreate = () => {
    setChecklistStamp(EMPTY_STAMP);
    setChecklistApprovalOpen(false);
    setChecklistAttachments([]);
    setChecklistPendingFiles([]);
    setChecklistItems([]);
    setChecklistForm({
      planId: plans[0]?.planId ?? null,
      checklistNo: "",
      title: "내부심사 체크시트",
      auditDate: new Date().toISOString().slice(0, 10),
      auditorName: plans[0]?.leadAuditorName || "",
      status: "DRAFT",
      remark: "",
    });
    setChecklistHint("저장하면 상세보기로 이동합니다.");
    setChecklistView({ type: "create" });
  };

  const fillChecklistForm = (row: InternalAuditChecklist) => {
    setChecklistStamp(stampFrom(row));
    setChecklistApprovalOpen(false);
    setChecklistHint("");
    setChecklistAttachments(row.attachments || []);
    setChecklistPendingFiles([]);
    setChecklistItems(parseItems(row.itemsJson));
    setChecklistForm({
      planId: row.planId ?? null,
      checklistNo: row.checklistNo || "",
      title: row.title || "",
      auditDate: row.auditDate || "",
      auditorName: row.auditorName || "",
      status: row.status || "DRAFT",
      remark: row.remark || "",
    });
  };

  const openChecklistDetail = (row: InternalAuditChecklist) => {
    fillChecklistForm(row);
    setChecklistView({ type: "detail", checklistId: row.checklistId });
  };

  const saveChecklist = async (e: FormEvent) => {
    e.preventDefault();
    if (isApproved(checklistForm.status)) {
      reportError(setError, "승인 완료된 체크시트는 수정할 수 없습니다.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...checklistForm,
        itemsJson: JSON.stringify(checklistItems),
      };
      let checklistId = checklistEditId;
      let saved: InternalAuditChecklist | null = null;
      if (checklistId != null) {
        saved = await updateInternalAuditChecklist(checklistId, payload);
      } else {
        saved = await createInternalAuditChecklist(payload);
        checklistId = saved.checklistId;
        window.alert(
          `체크시트번호 ${saved.checklistNo || "-"}로 등록되었습니다.`,
        );
      }
      if (checklistPendingFiles.length > 0 && checklistId != null) {
        const uploaded = await uploadInternalAuditChecklistAttachments(
          checklistId,
          checklistPendingFiles,
        );
        saved = {
          ...saved,
          attachments: [...(saved.attachments || []), ...uploaded],
        };
        setChecklistPendingFiles([]);
      }
      fillChecklistForm(saved);
      setChecklistForm((p) => ({
        ...p,
        checklistNo: saved!.checklistNo || p.checklistNo,
        status: saved!.status || p.status,
      }));
      setChecklistStamp(stampFrom(saved));
      setChecklistAttachments(saved.attachments || []);
      setChecklistView({ type: "detail", checklistId: saved.checklistId });
      await loadChecklists(checklistFilterPlanId);
    } catch {
      reportError(setError, "체크시트 저장에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  const approveChecklist = async () => {
    if (checklistEditId == null) return;
    if (!canApproveDirect) {
      reportError(setError, "바로 승인은 오너/팀장만 가능합니다.");
      return;
    }
    if (
      !window.confirm(
        "체크시트를 바로 승인할까요? 승인 후에는 수정·삭제할 수 없습니다.",
      )
    ) {
      return;
    }
    try {
      setApproving(true);
      setError("");
      const saved = await approveInternalAuditChecklist(checklistEditId);
      setChecklistStamp(stampFrom(saved));
      setChecklistForm((p) => ({ ...p, status: saved.status || "APPROVED" }));
      setApprovalMessage("바로 승인되었습니다.");
      await loadChecklists(checklistFilterPlanId);
    } catch (err: unknown) {
      reportError(setError, apiErrorMessage(err, "바로 승인에 실패했습니다."));
    } finally {
      setApproving(false);
    }
  };

  const revokeChecklist = async () => {
    if (checklistEditId == null) return;
    if (!window.confirm("승인을 철회할까요?\n승인란·검토란이 지워집니다."))
      return;
    try {
      setApproving(true);
      setError("");
      const saved = await revokeInternalAuditChecklist(checklistEditId);
      setChecklistStamp(stampFrom(saved));
      setChecklistForm((p) => ({ ...p, status: saved.status || "DRAFT" }));
      setApprovalMessage("승인이 철회되었습니다.");
      await loadChecklists(checklistFilterPlanId);
    } catch (err: unknown) {
      reportError(setError, apiErrorMessage(err, "승인 철회에 실패했습니다."));
    } finally {
      setApproving(false);
    }
  };

  const downloadChecklistPdf = async () => {
    if (checklistEditId == null) return;
    try {
      setPdfDownloading(true);
      const name = checklistForm.checklistNo?.trim() || String(checklistEditId);
      await downloadInternalAuditChecklistPdf(
        checklistEditId,
        `내부감사체크리스트_${name}.pdf`,
      );
    } catch (err) {
      notifyError(
        err instanceof Error
          ? err.message
          : "PDF 다운로드에 실패했습니다. Gotenberg 실행 여부를 확인해 주세요.",
      );
    } finally {
      setPdfDownloading(false);
    }
  };

  const handleChecklistUploadNow = async (files: FileList | null) => {
    if (!files?.length || checklistAttachmentsLocked) return;
    const selected = Array.from(files);
    if (checklistFileRef.current) checklistFileRef.current.value = "";
    if (checklistEditId == null) {
      setChecklistPendingFiles((prev) => [...prev, ...selected]);
      return;
    }
    setSaving(true);
    try {
      const uploaded = await uploadInternalAuditChecklistAttachments(
        checklistEditId,
        selected,
      );
      setChecklistAttachments((prev) => [...prev, ...uploaded]);
      await loadChecklists(checklistFilterPlanId);
    } catch {
      reportError(setError, "첨부파일 업로드에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteChecklistAttachment = async (
    attachment: InternalAuditAttachment,
  ) => {
    if (checklistEditId == null || checklistAttachmentsLocked) return;
    if (!window.confirm(`"${attachment.fileName}" 첨부를 삭제할까요?`)) return;
    setSaving(true);
    try {
      await deleteInternalAuditChecklistAttachment(
        checklistEditId,
        attachment.attachmentId,
      );
      setChecklistAttachments((prev) =>
        prev.filter((a) => a.attachmentId !== attachment.attachmentId),
      );
      await loadChecklists(checklistFilterPlanId);
    } catch {
      reportError(setError, "첨부파일 삭제에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  const openReportCreate = () => {
    const plan = plans[0];
    const linkedChecklist =
      checklists.find((c) => c.planId === plan?.planId) ||
      checklists[0] ||
      null;
    const items = linkedChecklist ? parseItems(linkedChecklist.itemsJson) : [];
    const counts = countResults(items);
    setReportStamp(EMPTY_STAMP);
    setReportApprovalOpen(false);
    setReportAttachments([]);
    setReportPendingFiles([]);
    setReportForm({
      planId: plan?.planId ?? null,
      checklistId: linkedChecklist?.checklistId ?? null,
      reportNo: "",
      title: "내부심사 보고서",
      auditDate:
        linkedChecklist?.auditDate || new Date().toISOString().slice(0, 10),
      reportDate: new Date().toISOString().slice(0, 10),
      scopeSummary: plan?.scopeText || "",
      teamText: plan?.auditorNames || plan?.leadAuditorName || "",
      conformCount: counts.conform,
      ofiCount: counts.ofi,
      ncCount: counts.nc,
      findingsText: "",
      conclusion: "",
      followUpDue: "",
      ncrRefs: "",
      auditeeConfirm: "",
      status: "DRAFT",
      remark: "",
    });
    setReportHint("저장하면 상세보기로 이동합니다.");
    setReportView({ type: "create" });
  };

  const fillReportForm = (row: InternalAuditReport) => {
    setReportStamp(stampFrom(row));
    setReportApprovalOpen(false);
    setReportHint("");
    setReportAttachments(row.attachments || []);
    setReportPendingFiles([]);
    setReportForm({
      planId: row.planId ?? null,
      checklistId: row.checklistId ?? null,
      reportNo: row.reportNo || "",
      title: row.title || "",
      auditDate: row.auditDate || "",
      reportDate: row.reportDate || "",
      scopeSummary: row.scopeSummary || "",
      teamText: row.teamText || "",
      conformCount: row.conformCount ?? 0,
      ofiCount: row.ofiCount ?? 0,
      ncCount: row.ncCount ?? 0,
      findingsText: row.findingsText || "",
      conclusion: row.conclusion || "",
      followUpDue: row.followUpDue || "",
      ncrRefs: row.ncrRefs || "",
      auditeeConfirm: row.auditeeConfirm || "",
      status: row.status || "DRAFT",
      remark: row.remark || "",
    });
  };

  const openReportDetail = (row: InternalAuditReport) => {
    fillReportForm(row);
    setReportView({ type: "detail", reportId: row.reportId });
  };

  const saveReport = async (e: FormEvent) => {
    e.preventDefault();
    if (isApproved(reportForm.status)) {
      reportError(setError, "승인 완료된 보고서는 수정할 수 없습니다.");
      return;
    }
    setSaving(true);
    try {
      let reportId = reportEditId;
      let saved: InternalAuditReport | null = null;
      if (reportId != null) {
        saved = await updateInternalAuditReport(reportId, reportForm);
      } else {
        saved = await createInternalAuditReport(reportForm);
        reportId = saved.reportId;
        window.alert(`보고서번호 ${saved.reportNo || "-"}로 등록되었습니다.`);
      }
      if (reportPendingFiles.length > 0 && reportId != null) {
        const uploaded = await uploadInternalAuditReportAttachments(
          reportId,
          reportPendingFiles,
        );
        saved = {
          ...saved,
          attachments: [...(saved.attachments || []), ...uploaded],
        };
        setReportPendingFiles([]);
      }
      fillReportForm(saved);
      setReportForm((p) => ({
        ...p,
        reportNo: saved!.reportNo || p.reportNo,
        status: saved!.status || p.status,
      }));
      setReportStamp(stampFrom(saved));
      setReportAttachments(saved.attachments || []);
      setReportView({ type: "detail", reportId: saved.reportId });
      await loadReports(reportFilterPlanId);
    } catch {
      reportError(setError, "심사 보고서 저장에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  const approveReport = async () => {
    if (reportEditId == null) return;
    if (!canApproveDirect) {
      reportError(setError, "바로 승인은 오너/팀장만 가능합니다.");
      return;
    }
    if (
      !window.confirm(
        "보고서를 바로 승인할까요? 승인 후에는 수정·삭제할 수 없습니다.",
      )
    ) {
      return;
    }
    try {
      setApproving(true);
      setError("");
      const saved = await approveInternalAuditReport(reportEditId);
      setReportStamp(stampFrom(saved));
      setReportForm((p) => ({ ...p, status: saved.status || "APPROVED" }));
      setApprovalMessage("바로 승인되었습니다.");
      await loadReports(reportFilterPlanId);
    } catch (err: unknown) {
      reportError(setError, apiErrorMessage(err, "바로 승인에 실패했습니다."));
    } finally {
      setApproving(false);
    }
  };

  const revokeReport = async () => {
    if (reportEditId == null) return;
    if (!window.confirm("승인을 철회할까요?\n승인란·검토란이 지워집니다."))
      return;
    try {
      setApproving(true);
      setError("");
      const saved = await revokeInternalAuditReport(reportEditId);
      setReportStamp(stampFrom(saved));
      setReportForm((p) => ({ ...p, status: saved.status || "DRAFT" }));
      setApprovalMessage("승인이 철회되었습니다.");
      await loadReports(reportFilterPlanId);
    } catch (err: unknown) {
      reportError(setError, apiErrorMessage(err, "승인 철회에 실패했습니다."));
    } finally {
      setApproving(false);
    }
  };

  const downloadReportPdf = async () => {
    if (reportEditId == null) return;
    try {
      setPdfDownloading(true);
      const name = reportForm.reportNo?.trim() || String(reportEditId);
      await downloadInternalAuditReportPdf(
        reportEditId,
        `내부감사보고서_${name}.pdf`,
      );
    } catch (err) {
      notifyError(
        err instanceof Error
          ? err.message
          : "PDF 다운로드에 실패했습니다. Gotenberg 실행 여부를 확인해 주세요.",
      );
    } finally {
      setPdfDownloading(false);
    }
  };

  const handleReportUploadNow = async (files: FileList | null) => {
    if (!files?.length || reportAttachmentsLocked) return;
    const selected = Array.from(files);
    if (reportFileRef.current) reportFileRef.current.value = "";
    if (reportEditId == null) {
      setReportPendingFiles((prev) => [...prev, ...selected]);
      return;
    }
    setSaving(true);
    try {
      const uploaded = await uploadInternalAuditReportAttachments(
        reportEditId,
        selected,
      );
      setReportAttachments((prev) => [...prev, ...uploaded]);
      await loadReports(reportFilterPlanId);
    } catch {
      reportError(setError, "첨부파일 업로드에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteReportAttachment = async (
    attachment: InternalAuditAttachment,
  ) => {
    if (reportEditId == null || reportAttachmentsLocked) return;
    if (!window.confirm(`"${attachment.fileName}" 첨부를 삭제할까요?`)) return;
    setSaving(true);
    try {
      await deleteInternalAuditReportAttachment(
        reportEditId,
        attachment.attachmentId,
      );
      setReportAttachments((prev) =>
        prev.filter((a) => a.attachmentId !== attachment.attachmentId),
      );
      await loadReports(reportFilterPlanId);
    } catch {
      reportError(setError, "첨부파일 삭제에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  const fillCountsFromChecklist = () => {
    if (!reportForm.checklistId) return;
    const row = checklists.find(
      (c) => c.checklistId === reportForm.checklistId,
    );
    if (!row) return;
    const counts = countResults(parseItems(row.itemsJson));
    setReportForm((p) => ({
      ...p,
      conformCount: counts.conform,
      ofiCount: counts.ofi,
      ncCount: counts.nc,
    }));
  };

  const renderPlanDetailActions = () => (
    <div
      className="ca-toolbar-actions"
      style={{ display: "flex", gap: 8, flexWrap: "wrap" }}
    >
      {isApproved(planForm.status) ? (
        <button
          type="button"
          className="ncr-btn is-primary"
          disabled={pdfDownloading}
          onClick={() => void downloadPlanPdf()}
        >
          {pdfDownloading ? "PDF 생성 중…" : "PDF 다운로드"}
        </button>
      ) : null}
      {planStamp.canRevoke ? (
        <button
          type="button"
          className="ncr-btn is-danger"
          disabled={approving}
          onClick={() => void revokePlan()}
        >
          {approving ? "철회 중..." : "승인 철회"}
        </button>
      ) : null}
      {planStamp.approvalInProgress ? (
        <p
          className="quality-form__hint"
          style={{ margin: 0, flexBasis: "100%" }}
        >
          결재가 진행 중입니다. 내용을 바꾸려면 결재 요청을 취소한 뒤 수정하고
          다시 요청하세요.
        </p>
      ) : null}
      <ApprovalCancelButton
        detail={planStamp}
        className="ncr-btn is-secondary"
        disabled={approving}
        onCancelled={async () => {
          await loadPlans();
        }}
      />
      {!isApproved(planForm.status) && !planStamp.approvalInProgress ? (
        <>
          <button
            type="button"
            className="ncr-btn is-secondary"
            onClick={() => {
              if (planEditId == null) return;
              setPlanView({ type: "edit", planId: planEditId });
            }}
          >
            수정
          </button>
          <button
            type="button"
            className="ncr-btn is-primary"
            disabled={approving}
            onClick={() => {
              setApprovalMessage("");
              setPlanApprovalOpen(true);
            }}
          >
            결재 요청
          </button>
          {canApproveDirect ? (
            <button
              type="button"
              className="ncr-btn is-approve"
              disabled={approving}
              onClick={() => void approvePlan()}
            >
              {approving ? "승인 중..." : "승인"}
            </button>
          ) : null}
          <button
            type="button"
            className="ncr-btn is-danger"
            disabled={approving}
            onClick={() => {
              if (planEditId == null) return;
              if (!window.confirm("삭제할까요?")) return;
              void deleteInternalAuditPlan(planEditId).then(() => {
                setPlanView({ type: "list" });
                void loadPlans(planKeyword);
              });
            }}
          >
            삭제
          </button>
        </>
      ) : null}
      <button
        type="button"
        className="ncr-btn is-secondary"
        onClick={() => setPlanView({ type: "list" })}
      >
        목록
      </button>
    </div>
  );

  const renderPlanEditActions = () => (
    <div className="quality-form__actions">
      <button type="submit" className="ncr-btn is-primary" disabled={saving}>
        저장
      </button>
      <button
        type="button"
        className="ncr-btn is-secondary"
        onClick={() =>
          planView.type === "edit" && planEditId != null
            ? setPlanView({ type: "detail", planId: planEditId })
            : setPlanView({ type: "list" })
        }
      >
        {planView.type === "edit" ? "상세" : "목록"}
      </button>
    </div>
  );

  const renderChecklistDetailActions = () => (
    <div
      className="ca-toolbar-actions"
      style={{ display: "flex", gap: 8, flexWrap: "wrap" }}
    >
      {isApproved(checklistForm.status) ? (
        <button
          type="button"
          className="ncr-btn is-primary"
          disabled={pdfDownloading}
          onClick={() => void downloadChecklistPdf()}
        >
          {pdfDownloading ? "PDF 생성 중…" : "PDF 다운로드"}
        </button>
      ) : null}
      {checklistStamp.canRevoke ? (
        <button
          type="button"
          className="ncr-btn is-danger"
          disabled={approving}
          onClick={() => void revokeChecklist()}
        >
          {approving ? "철회 중..." : "승인 철회"}
        </button>
      ) : null}
      {checklistStamp.approvalInProgress ? (
        <p
          className="quality-form__hint"
          style={{ margin: 0, flexBasis: "100%" }}
        >
          결재가 진행 중입니다. 내용을 바꾸려면 결재 요청을 취소한 뒤 수정하고
          다시 요청하세요.
        </p>
      ) : null}
      <ApprovalCancelButton
        detail={checklistStamp}
        className="ncr-btn is-secondary"
        disabled={approving}
        onCancelled={async () => {
          await loadChecklists();
        }}
      />
      {!isApproved(checklistForm.status) &&
      !checklistStamp.approvalInProgress ? (
        <>
          <button
            type="button"
            className="ncr-btn is-secondary"
            onClick={() => {
              if (checklistEditId == null) return;
              setChecklistView({
                type: "edit",
                checklistId: checklistEditId,
              });
            }}
          >
            수정
          </button>
          <button
            type="button"
            className="ncr-btn is-primary"
            disabled={approving}
            onClick={() => {
              setApprovalMessage("");
              setChecklistApprovalOpen(true);
            }}
          >
            결재 요청
          </button>
          {canApproveDirect ? (
            <button
              type="button"
              className="ncr-btn is-approve"
              disabled={approving}
              onClick={() => void approveChecklist()}
            >
              {approving ? "승인 중..." : "승인"}
            </button>
          ) : null}
          <button
            type="button"
            className="ncr-btn is-danger"
            disabled={approving}
            onClick={() => {
              if (checklistEditId == null) return;
              if (!window.confirm("삭제할까요?")) return;
              void deleteInternalAuditChecklist(checklistEditId).then(() => {
                setChecklistView({ type: "list" });
                void loadChecklists(checklistFilterPlanId);
              });
            }}
          >
            삭제
          </button>
        </>
      ) : null}
      <button
        type="button"
        className="ncr-btn is-secondary"
        onClick={() => setChecklistView({ type: "list" })}
      >
        목록
      </button>
    </div>
  );

  const renderChecklistEditActions = () => (
    <div className="quality-form__actions">
      <button type="submit" className="ncr-btn is-primary" disabled={saving}>
        저장
      </button>
      <button
        type="button"
        className="ncr-btn is-secondary"
        onClick={() =>
          checklistView.type === "edit" && checklistEditId != null
            ? setChecklistView({
                type: "detail",
                checklistId: checklistEditId,
              })
            : setChecklistView({ type: "list" })
        }
      >
        {checklistView.type === "edit" ? "상세" : "목록"}
      </button>
    </div>
  );

  const renderReportDetailActions = () => (
    <div
      className="ca-toolbar-actions"
      style={{ display: "flex", gap: 8, flexWrap: "wrap" }}
    >
      {isApproved(reportForm.status) ? (
        <button
          type="button"
          className="ncr-btn is-primary"
          disabled={pdfDownloading}
          onClick={() => void downloadReportPdf()}
        >
          {pdfDownloading ? "PDF 생성 중…" : "PDF 다운로드"}
        </button>
      ) : null}
      {reportStamp.canRevoke ? (
        <button
          type="button"
          className="ncr-btn is-danger"
          disabled={approving}
          onClick={() => void revokeReport()}
        >
          {approving ? "철회 중..." : "승인 철회"}
        </button>
      ) : null}
      {reportStamp.approvalInProgress ? (
        <p
          className="quality-form__hint"
          style={{ margin: 0, flexBasis: "100%" }}
        >
          결재가 진행 중입니다. 내용을 바꾸려면 결재 요청을 취소한 뒤 수정하고
          다시 요청하세요.
        </p>
      ) : null}
      <ApprovalCancelButton
        detail={reportStamp}
        className="ncr-btn is-secondary"
        disabled={approving}
        onCancelled={async () => {
          await loadReports();
        }}
      />
      {!isApproved(reportForm.status) && !reportStamp.approvalInProgress ? (
        <>
          <button
            type="button"
            className="ncr-btn is-secondary"
            onClick={() => {
              if (reportEditId == null) return;
              setReportView({
                type: "edit",
                reportId: reportEditId,
              });
            }}
          >
            수정
          </button>
          <button
            type="button"
            className="ncr-btn is-primary"
            disabled={approving}
            onClick={() => {
              setApprovalMessage("");
              setReportApprovalOpen(true);
            }}
          >
            결재 요청
          </button>
          {canApproveDirect ? (
            <button
              type="button"
              className="ncr-btn is-approve"
              disabled={approving}
              onClick={() => void approveReport()}
            >
              {approving ? "승인 중..." : "승인"}
            </button>
          ) : null}
          <button
            type="button"
            className="ncr-btn is-danger"
            disabled={approving}
            onClick={() => {
              if (reportEditId == null) return;
              if (!window.confirm("삭제할까요?")) return;
              void deleteInternalAuditReport(reportEditId).then(() => {
                setReportView({ type: "list" });
                void loadReports(reportFilterPlanId);
              });
            }}
          >
            삭제
          </button>
        </>
      ) : null}
      <button
        type="button"
        className="ncr-btn is-secondary"
        onClick={() => setReportView({ type: "list" })}
      >
        목록
      </button>
    </div>
  );

  const renderReportEditActions = () => (
    <div className="quality-form__actions">
      <button type="submit" className="ncr-btn is-primary" disabled={saving}>
        저장
      </button>
      <button
        type="button"
        className="ncr-btn is-secondary"
        onClick={() =>
          reportView.type === "edit" && reportEditId != null
            ? setReportView({
                type: "detail",
                reportId: reportEditId,
              })
            : setReportView({ type: "list" })
        }
      >
        {reportView.type === "edit" ? "상세" : "목록"}
      </button>
    </div>
  );

  return (
    <main className="quality-shell sg-panel">
      <div className="quality-shell__toolbar">
        <div>
          <h2>내부심사</h2>
          <p>심사 계획서 · 체크시트 · 심사 보고서 (ISO 9001 / KS)</p>
        </div>
      </div>

      <div className="quality-tabs">
        {(
          [
            ["plan", "심사 계획서"],
            ["checklist", "체크시트"],
            ["report", "심사 보고서"],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={`quality-tabs__btn${tab === key ? " is-active" : ""}`}
            onClick={() => {
              setTab(key);
              resetForms();
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {error && <p className="quality-error">{error}</p>}
      {approvalMessage ? (
        <p className="ca-approval-msg">{approvalMessage}</p>
      ) : null}

      {tab === "plan" && (
        <>
          {!showPlanForm ? (
            <>
              <div className="quality-shell__toolbar">
                <input
                  value={planKeyword}
                  onChange={(e) => setPlanKeyword(e.target.value)}
                  placeholder="번호 / 제목 / 피심사부서 / 심사원 검색"
                  style={{
                    height: 34,
                    padding: "0 10px",
                    borderRadius: 8,
                    border: "1px solid #cbd5e1",
                  }}
                />
                <button
                  type="button"
                  className="ncr-btn is-primary"
                  onClick={() => void openPlanCreate()}
                >
                  계획서 등록
                </button>
              </div>
              <div className="quality-table-wrap">
                <table className="quality-table">
                  <thead>
                    <tr>
                      <th>번호</th>
                      <th>연도</th>
                      <th>구분</th>
                      <th>제목</th>
                      <th>피심사</th>
                      <th>주심사원</th>
                      <th>상태</th>
                      <th>첨부</th>
                      <th>관리</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plans.length === 0 ? (
                      <tr>
                        <td colSpan={9}>등록된 계획서가 없습니다.</td>
                      </tr>
                    ) : (
                      plans.map((row) => (
                        <tr
                          key={row.planId}
                          onClick={() => openPlanDetail(row)}
                        >
                          <td>{row.planNo || "-"}</td>
                          <td>{row.planYear || "-"}</td>
                          <td>
                            {row.auditType === "SPECIAL" ? "특별" : "정기"}
                          </td>
                          <td>{row.title || "-"}</td>
                          <td>{row.auditeeDept || "-"}</td>
                          <td>{row.leadAuditorName || "-"}</td>
                          <td>
                            <span
                              className={`quality-badge is-${row.approvalInProgress ? "pending" : (row.status || "draft").toLowerCase()}`}
                            >
                              {planStatusLabel(
                                row.status,
                                row.approvalInProgress,
                              )}
                            </span>
                          </td>
                          <td>{row.attachmentCount ?? 0}</td>
                          <td>
                            {!isApproved(row.status) &&
                            !row.approvalInProgress ? (
                              <button
                                type="button"
                                className="ncr-btn is-danger"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (
                                    !window.confirm(
                                      "계획서와 연결된 체크시트·보고서가 함께 삭제됩니다. 계속할까요?",
                                    )
                                  )
                                    return;
                                  void deleteInternalAuditPlan(row.planId).then(
                                    () => {
                                      void loadPlans(planKeyword);
                                      void loadChecklists(
                                        checklistFilterPlanId,
                                      );
                                      void loadReports(reportFilterPlanId);
                                    },
                                  );
                                }}
                              >
                                삭제
                              </button>
                            ) : (
                              "-"
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <form
              className="quality-form"
              onSubmit={isPlanEditing ? savePlan : (e) => e.preventDefault()}
            >
              {isPlanDetail ? (
                <div
                  className="quality-shell__toolbar"
                  style={{ marginBottom: 12 }}
                >
                  <div>
                    <h3 style={{ margin: 0 }}>계획서 상세</h3>
                    <p
                      className="quality-form__hint"
                      style={{ margin: "4px 0 0" }}
                    >
                      {planForm.planNo || "-"} ·{" "}
                      {planStamp.approvalInProgress
                        ? "결재 요청 중"
                        : isApproved(planForm.status)
                          ? "승인완료"
                          : "작성중"}
                    </p>
                  </div>
                  {renderPlanDetailActions()}
                </div>
              ) : (
                renderPlanEditActions()
              )}
              <fieldset
                disabled={isPlanDetail}
                style={{ border: 0, margin: 0, padding: 0, minInlineSize: 0 }}
              >
                <section className="quality-form__section">
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 16,
                      alignItems: "flex-start",
                      marginBottom: 12,
                    }}
                  >
                    <h3 style={{ margin: 0 }}>심사 계획서</h3>
                    <StampBox
                      authorName={planStamp.authorName}
                      reviewerName={planStamp.reviewerName}
                      approverName={planStamp.approverName}
                    />
                  </div>
                  <div className="quality-form__grid">
                    <label>
                      계획서번호
                      <input
                        value={planEditId == null ? "" : planForm.planNo || ""}
                        readOnly
                        placeholder={
                          planEditId == null ? "저장 시 자동 발급" : undefined
                        }
                      />
                      {planEditId == null && planHint ? (
                        <span className="quality-form__hint">{planHint}</span>
                      ) : null}
                    </label>
                    <label>
                      연도
                      <input
                        type="number"
                        min={2000}
                        max={2100}
                        value={planForm.planYear || ""}
                        onChange={(e) =>
                          setPlanForm((p) => ({
                            ...p,
                            planYear: Number(e.target.value),
                          }))
                        }
                      />
                    </label>
                    <label>
                      구분
                      <select
                        value={planForm.auditType || "REGULAR"}
                        onChange={(e) =>
                          setPlanForm((p) => ({
                            ...p,
                            auditType: e.target.value,
                          }))
                        }
                      >
                        <option value="REGULAR">정기</option>
                        <option value="SPECIAL">특별</option>
                      </select>
                    </label>
                    <label className="is-span-2">
                      제목
                      <input
                        value={planForm.title || ""}
                        onChange={(e) =>
                          setPlanForm((p) => ({ ...p, title: e.target.value }))
                        }
                      />
                    </label>
                    <label>
                      기간 시작
                      <input
                        type="date"
                        value={planForm.periodFrom || ""}
                        onChange={(e) =>
                          setPlanForm((p) => ({
                            ...p,
                            periodFrom: e.target.value,
                          }))
                        }
                      />
                    </label>
                    <label>
                      기간 종료
                      <input
                        type="date"
                        value={planForm.periodTo || ""}
                        onChange={(e) =>
                          setPlanForm((p) => ({
                            ...p,
                            periodTo: e.target.value,
                          }))
                        }
                      />
                    </label>
                    <label>
                      피심사 부서/프로세스
                      <input
                        value={planForm.auditeeDept || ""}
                        onChange={(e) =>
                          setPlanForm((p) => ({
                            ...p,
                            auditeeDept: e.target.value,
                          }))
                        }
                      />
                    </label>
                    <label>
                      피심사 책임자
                      <input
                        value={planForm.auditeeManager || ""}
                        onChange={(e) =>
                          setPlanForm((p) => ({
                            ...p,
                            auditeeManager: e.target.value,
                          }))
                        }
                      />
                    </label>
                    <label className="is-span-2">
                      심사 기준
                      <input
                        value={planForm.criteriaText || ""}
                        onChange={(e) =>
                          setPlanForm((p) => ({
                            ...p,
                            criteriaText: e.target.value,
                          }))
                        }
                      />
                    </label>
                    <label className="is-span-2">
                      목적
                      <textarea
                        rows={2}
                        value={planForm.purpose || ""}
                        onChange={(e) =>
                          setPlanForm((p) => ({
                            ...p,
                            purpose: e.target.value,
                          }))
                        }
                      />
                    </label>
                    <label className="is-span-2">
                      범위
                      <textarea
                        rows={2}
                        value={planForm.scopeText || ""}
                        onChange={(e) =>
                          setPlanForm((p) => ({
                            ...p,
                            scopeText: e.target.value,
                          }))
                        }
                      />
                    </label>
                    <label className="is-span-2">
                      비고
                      <textarea
                        rows={2}
                        value={planForm.remark || ""}
                        onChange={(e) =>
                          setPlanForm((p) => ({ ...p, remark: e.target.value }))
                        }
                      />
                    </label>
                  </div>
                </section>

                <section className="quality-form__section">
                  <h3>주심사원 *</h3>
                  <p className="quality-form__hint">
                    피심사 부서와 독립된 직원을 선택하세요.
                  </p>
                  <div className="sg-member-picker">
                    {orgMembers.length === 0 ? (
                      <p className="quality-form__hint">
                        선택 가능한 직원이 없습니다.
                      </p>
                    ) : (
                      orgMembers.map((m) => {
                        const selected =
                          planForm.leadAuditorUserId === m.userId;
                        return (
                          <label
                            key={`lead-${m.userId}`}
                            className={`sg-member-picker__item${selected ? " is-selected" : ""}`}
                          >
                            <input
                              type="radio"
                              name="audit-lead"
                              checked={selected}
                              onChange={() =>
                                setPlanForm((p) => ({
                                  ...p,
                                  leadAuditorUserId: m.userId,
                                  auditorUserIds: Array.from(
                                    new Set([
                                      ...(p.auditorUserIds || []),
                                      m.userId,
                                    ]),
                                  ),
                                }))
                              }
                            />
                            <span className="sg-member-picker__avatar">
                              {memberName(m).slice(0, 1)}
                            </span>
                            <span className="sg-member-picker__text">
                              <strong>{memberName(m)}</strong>
                              <span>{memberMeta(m)}</span>
                            </span>
                          </label>
                        );
                      })
                    )}
                  </div>
                </section>

                <section className="quality-form__section">
                  <h3>심사팀원</h3>
                  <div className="sg-member-picker">
                    {orgMembers.map((m) => {
                      const checked = (planForm.auditorUserIds || []).includes(
                        m.userId,
                      );
                      const locked = m.userId === planForm.leadAuditorUserId;
                      return (
                        <label
                          key={`aud-${m.userId}`}
                          className={`sg-member-picker__item${checked ? " is-selected" : ""}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={locked}
                            onChange={() =>
                              setPlanForm((p) => ({
                                ...p,
                                auditorUserIds: toggleId(
                                  p.auditorUserIds || [],
                                  m.userId,
                                ),
                              }))
                            }
                          />
                          <span className="sg-member-picker__avatar">
                            {memberName(m).slice(0, 1)}
                          </span>
                          <span className="sg-member-picker__text">
                            <strong>
                              {memberName(m)}
                              {locked ? " (주심사원)" : ""}
                            </strong>
                            <span>{memberMeta(m)}</span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                  <div className="sg-member-chips">
                    {(planForm.auditorUserIds || []).map((id) => {
                      const m = orgMemberMap.get(id);
                      return (
                        <span key={id} className="sg-member-chip">
                          {m ? memberName(m) : `사용자 ${id}`}
                        </span>
                      );
                    })}
                  </div>
                </section>
              </fieldset>
              <section className="quality-form__section">
                <div
                  className="edu-plan-card__title"
                  style={{ marginBottom: 8 }}
                >
                  <h3 style={{ margin: 0 }}>첨부파일</h3>
                  {!planAttachmentsLocked ? (
                    <>
                      <button
                        type="button"
                        className="ncr-btn is-secondary"
                        disabled={saving}
                        onClick={() => planFileRef.current?.click()}
                      >
                        파일 추가
                      </button>
                      <input
                        ref={planFileRef}
                        type="file"
                        multiple
                        hidden
                        onChange={(e) =>
                          void handlePlanUploadNow(e.target.files)
                        }
                      />
                    </>
                  ) : null}
                </div>
                <p className="edu-plan-hint">
                  승인본/일정 근거 문서를 첨부할 수 있습니다.
                  {planEditId == null
                    ? " 신규 작성 시 저장과 함께 업로드됩니다."
                    : ""}
                </p>
                <ul className="edu-plan-files">
                  {planAttachments.map((file) => (
                    <li key={file.attachmentId}>
                      <div>
                        <strong>{file.fileName}</strong>
                        <span>{formatSize(file.fileSize)}</span>
                      </div>
                      <div className="edu-plan-files__actions">
                        <button
                          type="button"
                          className="ncr-btn is-secondary"
                          onClick={() =>
                            void downloadInternalAuditPlanAttachment(
                              planEditId!,
                              file.attachmentId,
                              file.fileName,
                            ).catch(() =>
                              reportError(
                                setError,
                                "첨부파일 다운로드에 실패했습니다.",
                              ),
                            )
                          }
                        >
                          다운로드
                        </button>
                        {!planAttachmentsLocked ? (
                          <button
                            type="button"
                            className="ncr-btn is-danger"
                            disabled={saving}
                            onClick={() =>
                              void handleDeletePlanAttachment(file)
                            }
                          >
                            삭제
                          </button>
                        ) : null}
                      </div>
                    </li>
                  ))}
                  {planPendingFiles.map((file, idx) => (
                    <li key={`pending-plan-${file.name}-${idx}`}>
                      <div>
                        <strong>{file.name}</strong>
                        <span>저장 대기 · {formatSize(file.size)}</span>
                      </div>
                      <button
                        type="button"
                        className="ncr-btn is-danger"
                        onClick={() =>
                          setPlanPendingFiles((prev) =>
                            prev.filter((_, i) => i !== idx),
                          )
                        }
                      >
                        제거
                      </button>
                    </li>
                  ))}
                  {planAttachments.length === 0 &&
                    planPendingFiles.length === 0 && (
                      <li className="is-empty">등록된 첨부파일이 없습니다.</li>
                    )}
                </ul>
              </section>

              {isPlanDetail ? (
                <div className="quality-form__actions">
                  {renderPlanDetailActions()}
                </div>
              ) : (
                renderPlanEditActions()
              )}

              {planEditId != null ? (
                <ApprovalRequestModal
                  open={planApprovalOpen}
                  titleDefault={`${planForm.title || "내부심사 계획서"} 결재`}
                  targetType={APPROVAL_TARGET_INTERNAL_AUDIT_PLAN}
                  targetId={planEditId}
                  onClose={() => setPlanApprovalOpen(false)}
                  onSubmitted={() => {
                    setPlanApprovalOpen(false);
                    setApprovalMessage(
                      "결재 요청이 등록되었습니다. 헤더의 결재 탭에서 확인할 수 있습니다.",
                    );
                    void loadPlans(planKeyword);
                  }}
                />
              ) : null}
            </form>
          )}
        </>
      )}

      {tab === "checklist" && (
        <>
          {!showChecklistForm ? (
            <>
              <div className="quality-shell__toolbar">
                <select
                  value={checklistFilterPlanId}
                  onChange={(e) =>
                    setChecklistFilterPlanId(
                      e.target.value ? Number(e.target.value) : "",
                    )
                  }
                  style={{
                    height: 34,
                    borderRadius: 8,
                    border: "1px solid #cbd5e1",
                    padding: "0 8px",
                  }}
                >
                  <option value="">전체 계획서</option>
                  {plans.map((p) => (
                    <option key={p.planId} value={p.planId}>
                      {p.planNo || p.title}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="ncr-btn is-primary"
                  onClick={() => void openChecklistCreate()}
                >
                  체크시트 등록
                </button>
              </div>
              <div className="quality-table-wrap">
                <table className="quality-table">
                  <thead>
                    <tr>
                      <th>번호</th>
                      <th>심사일</th>
                      <th>제목</th>
                      <th>연결 계획</th>
                      <th>심사원</th>
                      <th>상태</th>
                      <th>첨부</th>
                      <th>관리</th>
                    </tr>
                  </thead>
                  <tbody>
                    {checklists.length === 0 ? (
                      <tr>
                        <td colSpan={8}>등록된 체크시트가 없습니다.</td>
                      </tr>
                    ) : (
                      checklists.map((row) => (
                        <tr
                          key={row.checklistId}
                          onClick={() => openChecklistDetail(row)}
                        >
                          <td>{row.checklistNo || "-"}</td>
                          <td>{row.auditDate || "-"}</td>
                          <td>{row.title || "-"}</td>
                          <td>{row.planTitle || "-"}</td>
                          <td>{row.auditorName || "-"}</td>
                          <td>
                            <span
                              className={`quality-badge is-${row.approvalInProgress ? "pending" : (row.status || "draft").toLowerCase()}`}
                            >
                              {checklistStatusLabel(
                                row.status,
                                row.approvalInProgress,
                              )}
                            </span>
                          </td>
                          <td>{row.attachmentCount ?? 0}</td>
                          <td>
                            {!isApproved(row.status) &&
                            !row.approvalInProgress ? (
                              <button
                                type="button"
                                className="ncr-btn is-danger"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!window.confirm("삭제할까요?")) return;
                                  void deleteInternalAuditChecklist(
                                    row.checklistId,
                                  ).then(() =>
                                    loadChecklists(checklistFilterPlanId),
                                  );
                                }}
                              >
                                삭제
                              </button>
                            ) : (
                              "-"
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <form
              className="quality-form"
              onSubmit={
                isChecklistEditing ? saveChecklist : (e) => e.preventDefault()
              }
            >
              {isChecklistDetail ? (
                <div
                  className="quality-shell__toolbar"
                  style={{ marginBottom: 12 }}
                >
                  <div>
                    <h3 style={{ margin: 0 }}>체크시트 상세</h3>
                    <p
                      className="quality-form__hint"
                      style={{ margin: "4px 0 0" }}
                    >
                      {checklistForm.checklistNo || "-"} ·{" "}
                      {checklistStamp.approvalInProgress
                        ? "결재 요청 중"
                        : isApproved(checklistForm.status)
                          ? "승인완료"
                          : "작성중"}
                    </p>
                  </div>
                  {renderChecklistDetailActions()}
                </div>
              ) : (
                renderChecklistEditActions()
              )}
              <fieldset
                disabled={isChecklistDetail}
                style={{ border: 0, margin: 0, padding: 0, minInlineSize: 0 }}
              >
                <section className="quality-form__section">
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 16,
                      alignItems: "flex-start",
                      marginBottom: 12,
                    }}
                  >
                    <h3 style={{ margin: 0 }}>체크시트</h3>
                    <StampBox
                      authorName={checklistStamp.authorName}
                      reviewerName={checklistStamp.reviewerName}
                      approverName={checklistStamp.approverName}
                    />
                  </div>
                  <div className="quality-form__grid">
                    <label>
                      체크시트번호
                      <input
                        value={
                          checklistEditId == null
                            ? ""
                            : checklistForm.checklistNo || ""
                        }
                        readOnly
                        placeholder={
                          checklistEditId == null
                            ? "저장 시 자동 발급"
                            : undefined
                        }
                      />
                      {checklistEditId == null && checklistHint ? (
                        <span className="quality-form__hint">
                          {checklistHint}
                        </span>
                      ) : null}
                    </label>
                    <label>
                      연결 계획서
                      <select
                        value={checklistForm.planId ?? ""}
                        onChange={(e) =>
                          setChecklistForm((p) => ({
                            ...p,
                            planId: e.target.value
                              ? Number(e.target.value)
                              : null,
                          }))
                        }
                      >
                        <option value="">미연결</option>
                        {plans.map((p) => (
                          <option key={p.planId} value={p.planId}>
                            {p.planNo || p.title}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      심사일
                      <input
                        type="date"
                        value={checklistForm.auditDate || ""}
                        onChange={(e) =>
                          setChecklistForm((p) => ({
                            ...p,
                            auditDate: e.target.value,
                          }))
                        }
                      />
                    </label>
                    <label className="is-span-2">
                      제목
                      <input
                        value={checklistForm.title || ""}
                        onChange={(e) =>
                          setChecklistForm((p) => ({
                            ...p,
                            title: e.target.value,
                          }))
                        }
                      />
                    </label>
                    <label>
                      심사원
                      <input
                        value={checklistForm.auditorName || ""}
                        onChange={(e) =>
                          setChecklistForm((p) => ({
                            ...p,
                            auditorName: e.target.value,
                          }))
                        }
                      />
                    </label>
                  </div>
                  <div style={{ marginTop: 16 }}>
                    <div style={{ marginBottom: 8 }}>
                      <strong>점검 항목</strong>
                      <p
                        className="quality-form__hint"
                        style={{ margin: "4px 0 0" }}
                      >
                        필요한 항목을 추가해 한 줄로 작성합니다. ·{" "}
                        {checklistItems.length}건
                      </p>
                    </div>
                    <div className="edu-plan-table-wrap ia-checklist-wrap">
                      <table className="edu-plan-table ia-checklist-table">
                        <thead>
                          <tr>
                            <th className="col-no">No</th>
                            <th className="col-std">구분</th>
                            <th className="col-clause">조항/항목</th>
                            <th className="col-question">확인질문</th>
                            <th className="col-evidence">증빙</th>
                            <th className="col-result">판정</th>
                            <th className="col-finding">발견사항</th>
                            {!isChecklistDetail ? (
                              <th className="col-actions" />
                            ) : null}
                          </tr>
                        </thead>
                        <tbody>
                          {checklistItems.length === 0 ? (
                            <tr>
                              <td
                                colSpan={isChecklistDetail ? 7 : 8}
                                className="ia-checklist-empty"
                              >
                                등록된 점검 항목이 없습니다. 아래에서 항목을
                                추가해 주세요.
                              </td>
                            </tr>
                          ) : (
                            checklistItems.map((item, idx) => (
                              <tr key={item.id}>
                                <td className="is-center col-no">{idx + 1}</td>
                                <td className="col-std">
                                  <select
                                    value={item.standard}
                                    onChange={(e) =>
                                      setChecklistItems((prev) =>
                                        prev.map((r, i) =>
                                          i === idx
                                            ? {
                                                ...r,
                                                standard: e.target.value,
                                              }
                                            : r,
                                        ),
                                      )
                                    }
                                  >
                                    <option value="ISO">ISO</option>
                                    <option value="KS">KS</option>
                                    <option value="공통">공통</option>
                                  </select>
                                </td>
                                <td className="col-clause">
                                  <input
                                    value={item.clause}
                                    placeholder="조항/항목"
                                    onChange={(e) =>
                                      setChecklistItems((prev) =>
                                        prev.map((r, i) =>
                                          i === idx
                                            ? { ...r, clause: e.target.value }
                                            : r,
                                        ),
                                      )
                                    }
                                  />
                                </td>
                                <td className="col-question">
                                  <input
                                    value={item.question}
                                    placeholder="확인질문"
                                    onChange={(e) =>
                                      setChecklistItems((prev) =>
                                        prev.map((r, i) =>
                                          i === idx
                                            ? {
                                                ...r,
                                                question: e.target.value,
                                              }
                                            : r,
                                        ),
                                      )
                                    }
                                  />
                                </td>
                                <td className="col-evidence">
                                  <input
                                    value={item.evidence}
                                    placeholder="증빙"
                                    onChange={(e) =>
                                      setChecklistItems((prev) =>
                                        prev.map((r, i) =>
                                          i === idx
                                            ? {
                                                ...r,
                                                evidence: e.target.value,
                                              }
                                            : r,
                                        ),
                                      )
                                    }
                                  />
                                </td>
                                <td className="col-result">
                                  <select
                                    value={item.result}
                                    onChange={(e) =>
                                      setChecklistItems((prev) =>
                                        prev.map((r, i) =>
                                          i === idx
                                            ? { ...r, result: e.target.value }
                                            : r,
                                        ),
                                      )
                                    }
                                  >
                                    <option value="">미판정</option>
                                    <option value="CONFORM">적합</option>
                                    <option value="OFI">관찰(OFI)</option>
                                    <option value="NC">부적합</option>
                                  </select>
                                </td>
                                <td className="col-finding">
                                  <input
                                    value={item.finding}
                                    placeholder="발견사항"
                                    onChange={(e) =>
                                      setChecklistItems((prev) =>
                                        prev.map((r, i) =>
                                          i === idx
                                            ? {
                                                ...r,
                                                finding: e.target.value,
                                              }
                                            : r,
                                        ),
                                      )
                                    }
                                  />
                                </td>
                                {!isChecklistDetail ? (
                                  <td className="col-actions">
                                    <button
                                      type="button"
                                      className="ncr-btn is-danger ia-checklist-del"
                                      onClick={() =>
                                        setChecklistItems((prev) =>
                                          prev.filter((_, i) => i !== idx),
                                        )
                                      }
                                    >
                                      삭제
                                    </button>
                                  </td>
                                ) : null}
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                    {!isChecklistDetail ? (
                      <div className="edu-plan-table-footer">
                        <button
                          type="button"
                          className="ncr-btn is-secondary"
                          onClick={() =>
                            setChecklistItems((prev) => [
                              ...prev,
                              emptyChecklistItem(),
                            ])
                          }
                        >
                          항목 추가
                        </button>
                      </div>
                    ) : null}
                  </div>
                  <div className="quality-form__grid" style={{ marginTop: 12 }}>
                    <label className="is-span-2">
                      비고
                      <textarea
                        rows={2}
                        value={checklistForm.remark || ""}
                        onChange={(e) =>
                          setChecklistForm((p) => ({
                            ...p,
                            remark: e.target.value,
                          }))
                        }
                      />
                    </label>
                  </div>
                </section>
              </fieldset>
              <section className="quality-form__section">
                <div
                  className="edu-plan-card__title"
                  style={{ marginBottom: 8 }}
                >
                  <h3 style={{ margin: 0 }}>첨부파일</h3>
                  {!checklistAttachmentsLocked ? (
                    <>
                      <button
                        type="button"
                        className="ncr-btn is-secondary"
                        disabled={saving}
                        onClick={() => checklistFileRef.current?.click()}
                      >
                        파일 추가
                      </button>
                      <input
                        ref={checklistFileRef}
                        type="file"
                        multiple
                        hidden
                        onChange={(e) =>
                          void handleChecklistUploadNow(e.target.files)
                        }
                      />
                    </>
                  ) : null}
                </div>
                <p className="edu-plan-hint">
                  현장증빙/사진을 첨부할 수 있습니다.
                  {checklistEditId == null
                    ? " 신규 작성 시 저장과 함께 업로드됩니다."
                    : ""}
                </p>
                <ul className="edu-plan-files">
                  {checklistAttachments.map((file) => (
                    <li key={file.attachmentId}>
                      <div>
                        <strong>{file.fileName}</strong>
                        <span>{formatSize(file.fileSize)}</span>
                      </div>
                      <div className="edu-plan-files__actions">
                        <button
                          type="button"
                          className="ncr-btn is-secondary"
                          onClick={() =>
                            void downloadInternalAuditChecklistAttachment(
                              checklistEditId!,
                              file.attachmentId,
                              file.fileName,
                            ).catch(() =>
                              reportError(
                                setError,
                                "첨부파일 다운로드에 실패했습니다.",
                              ),
                            )
                          }
                        >
                          다운로드
                        </button>
                        {!checklistAttachmentsLocked ? (
                          <button
                            type="button"
                            className="ncr-btn is-danger"
                            disabled={saving}
                            onClick={() =>
                              void handleDeleteChecklistAttachment(file)
                            }
                          >
                            삭제
                          </button>
                        ) : null}
                      </div>
                    </li>
                  ))}
                  {checklistPendingFiles.map((file, idx) => (
                    <li key={`pending-checklist-${file.name}-${idx}`}>
                      <div>
                        <strong>{file.name}</strong>
                        <span>저장 대기 · {formatSize(file.size)}</span>
                      </div>
                      <button
                        type="button"
                        className="ncr-btn is-danger"
                        onClick={() =>
                          setChecklistPendingFiles((prev) =>
                            prev.filter((_, i) => i !== idx),
                          )
                        }
                      >
                        제거
                      </button>
                    </li>
                  ))}
                  {checklistAttachments.length === 0 &&
                    checklistPendingFiles.length === 0 && (
                      <li className="is-empty">등록된 첨부파일이 없습니다.</li>
                    )}
                </ul>
              </section>
              {isChecklistDetail ? (
                <div className="quality-form__actions">
                  {renderChecklistDetailActions()}
                </div>
              ) : (
                renderChecklistEditActions()
              )}
              {checklistEditId != null ? (
                <ApprovalRequestModal
                  open={checklistApprovalOpen}
                  titleDefault={`${checklistForm.title || "내부심사 체크시트"} 결재`}
                  targetType={APPROVAL_TARGET_INTERNAL_AUDIT_CHECKLIST}
                  targetId={checklistEditId}
                  onClose={() => setChecklistApprovalOpen(false)}
                  onSubmitted={() => {
                    setChecklistApprovalOpen(false);
                    setApprovalMessage(
                      "결재 요청이 등록되었습니다. 헤더의 결재 탭에서 확인할 수 있습니다.",
                    );
                    void loadChecklists(checklistFilterPlanId);
                  }}
                />
              ) : null}
            </form>
          )}
        </>
      )}

      {tab === "report" && (
        <>
          {!showReportForm ? (
            <>
              <div className="quality-shell__toolbar">
                <select
                  value={reportFilterPlanId}
                  onChange={(e) =>
                    setReportFilterPlanId(
                      e.target.value ? Number(e.target.value) : "",
                    )
                  }
                  style={{
                    height: 34,
                    borderRadius: 8,
                    border: "1px solid #cbd5e1",
                    padding: "0 8px",
                  }}
                >
                  <option value="">전체 계획서</option>
                  {plans.map((p) => (
                    <option key={p.planId} value={p.planId}>
                      {p.planNo || p.title}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="ncr-btn is-primary"
                  onClick={() => void openReportCreate()}
                >
                  보고서 등록
                </button>
              </div>
              <div className="quality-table-wrap">
                <table className="quality-table">
                  <thead>
                    <tr>
                      <th>번호</th>
                      <th>심사일</th>
                      <th>제목</th>
                      <th>적합/관찰/부적합</th>
                      <th>상태</th>
                      <th>첨부</th>
                      <th>관리</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reports.length === 0 ? (
                      <tr>
                        <td colSpan={7}>등록된 보고서가 없습니다.</td>
                      </tr>
                    ) : (
                      reports.map((row) => (
                        <tr
                          key={row.reportId}
                          onClick={() => openReportDetail(row)}
                        >
                          <td>{row.reportNo || "-"}</td>
                          <td>{row.auditDate || "-"}</td>
                          <td>{row.title || "-"}</td>
                          <td>
                            {row.conformCount ?? 0} / {row.ofiCount ?? 0} /{" "}
                            {row.ncCount ?? 0}
                          </td>
                          <td>
                            <span
                              className={`quality-badge is-${row.approvalInProgress ? "pending" : (row.status || "draft").toLowerCase()}`}
                            >
                              {reportStatusLabel(
                                row.status,
                                row.approvalInProgress,
                              )}
                            </span>
                          </td>
                          <td>{row.attachmentCount ?? 0}</td>
                          <td>
                            {!isApproved(row.status) &&
                            !row.approvalInProgress ? (
                              <button
                                type="button"
                                className="ncr-btn is-danger"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!window.confirm("삭제할까요?")) return;
                                  void deleteInternalAuditReport(
                                    row.reportId,
                                  ).then(() => loadReports(reportFilterPlanId));
                                }}
                              >
                                삭제
                              </button>
                            ) : (
                              "-"
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <form
              className="quality-form"
              onSubmit={
                isReportEditing ? saveReport : (e) => e.preventDefault()
              }
            >
              {isReportDetail ? (
                <div
                  className="quality-shell__toolbar"
                  style={{ marginBottom: 12 }}
                >
                  <div>
                    <h3 style={{ margin: 0 }}>보고서 상세</h3>
                    <p
                      className="quality-form__hint"
                      style={{ margin: "4px 0 0" }}
                    >
                      {reportForm.reportNo || "-"} ·{" "}
                      {reportStamp.approvalInProgress
                        ? "결재 요청 중"
                        : isApproved(reportForm.status)
                          ? "승인완료"
                          : "작성중"}
                    </p>
                  </div>
                  {renderReportDetailActions()}
                </div>
              ) : (
                renderReportEditActions()
              )}
              <fieldset
                disabled={isReportDetail}
                style={{ border: 0, margin: 0, padding: 0, minInlineSize: 0 }}
              >
                <section className="quality-form__section">
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 16,
                      alignItems: "flex-start",
                      marginBottom: 12,
                    }}
                  >
                    <h3 style={{ margin: 0 }}>심사 보고서</h3>
                    <StampBox
                      authorName={reportStamp.authorName}
                      reviewerName={reportStamp.reviewerName}
                      approverName={reportStamp.approverName}
                    />
                  </div>
                  <div className="quality-form__grid">
                    <label>
                      보고서번호
                      <input
                        value={
                          reportEditId == null ? "" : reportForm.reportNo || ""
                        }
                        readOnly
                        placeholder={
                          reportEditId == null ? "저장 시 자동 발급" : undefined
                        }
                      />
                      {reportEditId == null && reportHint ? (
                        <span className="quality-form__hint">{reportHint}</span>
                      ) : null}
                    </label>
                    <label>
                      연결 계획서
                      <select
                        value={reportForm.planId ?? ""}
                        onChange={(e) =>
                          setReportForm((p) => ({
                            ...p,
                            planId: e.target.value
                              ? Number(e.target.value)
                              : null,
                          }))
                        }
                      >
                        <option value="">미연결</option>
                        {plans.map((p) => (
                          <option key={p.planId} value={p.planId}>
                            {p.planNo || p.title}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      연결 체크시트
                      <select
                        value={reportForm.checklistId ?? ""}
                        onChange={(e) =>
                          setReportForm((p) => ({
                            ...p,
                            checklistId: e.target.value
                              ? Number(e.target.value)
                              : null,
                          }))
                        }
                      >
                        <option value="">미연결</option>
                        {checklists.map((c) => (
                          <option key={c.checklistId} value={c.checklistId}>
                            {c.checklistNo || c.title}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      심사일
                      <input
                        type="date"
                        value={reportForm.auditDate || ""}
                        onChange={(e) =>
                          setReportForm((p) => ({
                            ...p,
                            auditDate: e.target.value,
                          }))
                        }
                      />
                    </label>
                    <label>
                      보고일
                      <input
                        type="date"
                        value={reportForm.reportDate || ""}
                        onChange={(e) =>
                          setReportForm((p) => ({
                            ...p,
                            reportDate: e.target.value,
                          }))
                        }
                      />
                    </label>
                    <label className="is-span-2">
                      제목
                      <input
                        value={reportForm.title || ""}
                        onChange={(e) =>
                          setReportForm((p) => ({
                            ...p,
                            title: e.target.value,
                          }))
                        }
                      />
                    </label>
                    <label className="is-span-2">
                      범위 요약
                      <textarea
                        rows={2}
                        value={reportForm.scopeSummary || ""}
                        onChange={(e) =>
                          setReportForm((p) => ({
                            ...p,
                            scopeSummary: e.target.value,
                          }))
                        }
                      />
                    </label>
                    <label className="is-span-2">
                      심사팀
                      <input
                        value={reportForm.teamText || ""}
                        onChange={(e) =>
                          setReportForm((p) => ({
                            ...p,
                            teamText: e.target.value,
                          }))
                        }
                      />
                    </label>
                    <label>
                      적합
                      <input
                        type="number"
                        min={0}
                        value={reportForm.conformCount ?? 0}
                        onChange={(e) =>
                          setReportForm((p) => ({
                            ...p,
                            conformCount: Number(e.target.value),
                          }))
                        }
                      />
                    </label>
                    <label>
                      관찰(OFI)
                      <input
                        type="number"
                        min={0}
                        value={reportForm.ofiCount ?? 0}
                        onChange={(e) =>
                          setReportForm((p) => ({
                            ...p,
                            ofiCount: Number(e.target.value),
                          }))
                        }
                      />
                    </label>
                    <label>
                      부적합
                      <input
                        type="number"
                        min={0}
                        value={reportForm.ncCount ?? 0}
                        onChange={(e) =>
                          setReportForm((p) => ({
                            ...p,
                            ncCount: Number(e.target.value),
                          }))
                        }
                      />
                    </label>
                    <div style={{ display: "flex", alignItems: "end" }}>
                      <button
                        type="button"
                        className="ncr-btn is-secondary"
                        onClick={fillCountsFromChecklist}
                        disabled={!reportForm.checklistId}
                      >
                        체크시트에서 집계
                      </button>
                    </div>
                    <label className="is-span-2">
                      주요 발견사항
                      <textarea
                        rows={4}
                        value={reportForm.findingsText || ""}
                        onChange={(e) =>
                          setReportForm((p) => ({
                            ...p,
                            findingsText: e.target.value,
                          }))
                        }
                      />
                    </label>
                    <label className="is-span-2">
                      종합결론
                      <textarea
                        rows={3}
                        value={reportForm.conclusion || ""}
                        onChange={(e) =>
                          setReportForm((p) => ({
                            ...p,
                            conclusion: e.target.value,
                          }))
                        }
                      />
                    </label>
                    <label>
                      시정조치 기한
                      <input
                        type="date"
                        value={reportForm.followUpDue || ""}
                        onChange={(e) =>
                          setReportForm((p) => ({
                            ...p,
                            followUpDue: e.target.value,
                          }))
                        }
                      />
                    </label>
                    <label>
                      NCR 연결(번호)
                      <input
                        value={reportForm.ncrRefs || ""}
                        onChange={(e) =>
                          setReportForm((p) => ({
                            ...p,
                            ncrRefs: e.target.value,
                          }))
                        }
                        placeholder="예: NCR-2026-001"
                      />
                    </label>
                    <label>
                      피심사 확인
                      <input
                        value={reportForm.auditeeConfirm || ""}
                        onChange={(e) =>
                          setReportForm((p) => ({
                            ...p,
                            auditeeConfirm: e.target.value,
                          }))
                        }
                      />
                    </label>
                    <label className="is-span-2">
                      비고
                      <textarea
                        rows={2}
                        value={reportForm.remark || ""}
                        onChange={(e) =>
                          setReportForm((p) => ({
                            ...p,
                            remark: e.target.value,
                          }))
                        }
                      />
                    </label>
                  </div>
                </section>
              </fieldset>
              <section className="quality-form__section">
                <div
                  className="edu-plan-card__title"
                  style={{ marginBottom: 8 }}
                >
                  <h3 style={{ margin: 0 }}>첨부파일</h3>
                  {!reportAttachmentsLocked ? (
                    <>
                      <button
                        type="button"
                        className="ncr-btn is-secondary"
                        disabled={saving}
                        onClick={() => reportFileRef.current?.click()}
                      >
                        파일 추가
                      </button>
                      <input
                        ref={reportFileRef}
                        type="file"
                        multiple
                        hidden
                        onChange={(e) =>
                          void handleReportUploadNow(e.target.files)
                        }
                      />
                    </>
                  ) : null}
                </div>
                <p className="edu-plan-hint">
                  발행본/서명본을 첨부할 수 있습니다.
                  {reportEditId == null
                    ? " 신규 작성 시 저장과 함께 업로드됩니다."
                    : ""}
                </p>
                <ul className="edu-plan-files">
                  {reportAttachments.map((file) => (
                    <li key={file.attachmentId}>
                      <div>
                        <strong>{file.fileName}</strong>
                        <span>{formatSize(file.fileSize)}</span>
                      </div>
                      <div className="edu-plan-files__actions">
                        <button
                          type="button"
                          className="ncr-btn is-secondary"
                          onClick={() =>
                            void downloadInternalAuditReportAttachment(
                              reportEditId!,
                              file.attachmentId,
                              file.fileName,
                            ).catch(() =>
                              reportError(
                                setError,
                                "첨부파일 다운로드에 실패했습니다.",
                              ),
                            )
                          }
                        >
                          다운로드
                        </button>
                        {!reportAttachmentsLocked ? (
                          <button
                            type="button"
                            className="ncr-btn is-danger"
                            disabled={saving}
                            onClick={() =>
                              void handleDeleteReportAttachment(file)
                            }
                          >
                            삭제
                          </button>
                        ) : null}
                      </div>
                    </li>
                  ))}
                  {reportPendingFiles.map((file, idx) => (
                    <li key={`pending-report-${file.name}-${idx}`}>
                      <div>
                        <strong>{file.name}</strong>
                        <span>저장 대기 · {formatSize(file.size)}</span>
                      </div>
                      <button
                        type="button"
                        className="ncr-btn is-danger"
                        onClick={() =>
                          setReportPendingFiles((prev) =>
                            prev.filter((_, i) => i !== idx),
                          )
                        }
                      >
                        제거
                      </button>
                    </li>
                  ))}
                  {reportAttachments.length === 0 &&
                    reportPendingFiles.length === 0 && (
                      <li className="is-empty">등록된 첨부파일이 없습니다.</li>
                    )}
                </ul>
              </section>
              {isReportDetail ? (
                <div className="quality-form__actions">
                  {renderReportDetailActions()}
                </div>
              ) : (
                renderReportEditActions()
              )}
              {reportEditId != null ? (
                <ApprovalRequestModal
                  open={reportApprovalOpen}
                  titleDefault={`${reportForm.title || "내부심사 보고서"} 결재`}
                  targetType={APPROVAL_TARGET_INTERNAL_AUDIT_REPORT}
                  targetId={reportEditId}
                  onClose={() => setReportApprovalOpen(false)}
                  onSubmitted={() => {
                    setReportApprovalOpen(false);
                    setApprovalMessage(
                      "결재 요청이 등록되었습니다. 헤더의 결재 탭에서 확인할 수 있습니다.",
                    );
                    void loadReports(reportFilterPlanId);
                  }}
                />
              ) : null}
            </form>
          )}
        </>
      )}
    </main>
  );
}

export default InternalAuditPanel;
